import React, { useEffect, useRef, useState, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Modal, Button, Font, Link } from '@pepsico-ds/ui'
import { getCookie, COOKIE_NAME, useProgram } from 'ui/hooks/useProgram'
import { Box } from '@mui/material'
import { useLocation } from 'react-router-dom'
import './CookieDisclaimer.scss'

// Helper to delete cookie
function deleteProgramCookie() {
  const isSecure = typeof window !== 'undefined' && window.location?.protocol === 'https:'
  if (typeof document === 'undefined') return
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${isSecure ? '; Secure' : ''}`
}

function CookieDisclaimer() {
  const location = useLocation()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [policyOpen, setPolicyOpen] = useState(false)
  const policyTriggerRef = useRef(null)
  const { selectedProgram, setSelectedProgram } = useProgram()

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent')
    const sessionDismissed = sessionStorage.getItem('cookiePopoverDismissedThisSession')
    const isAuthRoute = location.pathname === '/login' || location?.pathname === '/logout'

    if (isAuthRoute) {
      setPopoverOpen(false)
      setPolicyOpen(false)
      return
    }

    // Show popover if:
    // 1. No consent OR user declined (give another chance on re-login)
    // 2. AND not dismissed this session (don't annoy on refresh/navigation)
    const shouldShowPopover = (!consent || consent === 'declined') && !sessionDismissed

    setPopoverOpen(Boolean(shouldShowPopover))
  }, [location?.pathname])

  const handleAcceptCookies = useCallback(() => {
    try {
      localStorage.setItem('cookieConsent', 'accepted')
      sessionStorage.setItem('cookiePopoverDismissedThisSession', 'true')
      setPopoverOpen(false)
      setPolicyOpen(false)

      if (selectedProgram?.id) {
        setSelectedProgram(selectedProgram)
      }
    } catch (e) {
      console.warn('Failed to set cookie consent', e)
    }
  }, [selectedProgram])

  const handleDeclineCookies = useCallback(() => {
    try {
      localStorage.setItem('cookieConsent', 'declined')
      sessionStorage.setItem('cookiePopoverDismissedThisSession', 'true')
      setPopoverOpen(false)
      setPolicyOpen(false)
      deleteProgramCookie()

      if (selectedProgram?.id) {
        setSelectedProgram(selectedProgram)
      }
    } catch (e) {
      console.warn('Failed to handle cookie decline', e)
    }
  }, [selectedProgram])

  const handleOpenPolicy = useCallback((e) => {
    e?.preventDefault?.()
    setPolicyOpen(true)
  }, [])

  const handleClosePolicy = useCallback(() => {
    setPolicyOpen(false)

    // If user closes modal without choice, treat as implicit decline
    // but keep popover visible for them to make explicit choice
    const consent = localStorage.getItem('cookieConsent')
    if (!consent) {
      setPopoverOpen(true)
    }

    // return focus to the trigger for a11y
    try {
      policyTriggerRef.current?.focus()
    } catch (e) {
      // ignore focus errors
    }
  }, [])

  // Inline styles to ensure proper positioning (failsafe if CSS doesn't load)
  const popoverStyles = {
    position: 'fixed',
    right: '24px',
    bottom: '24px',
    zIndex: 1400,
    width: '360px',
    maxWidth: 'calc(100vw - 48px)',
    background: '#ffffff',
    borderRadius: '8px',
    padding: '20px',
    border: '10px solid var(--color-border-default, #d7e5f8)',
    boxShadow: '0px 2px 24px 8px rgba(0, 92, 188, 0.05)',
    animation: 'cookie-popover-slide-in 300ms cubic-bezier(0.4, 0, 0.2, 1) both',
  }

  const contentStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px',
  }

  const actionsStyles = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-around',
    alignItems: 'center',
  }

  const popoverContent = popoverOpen && (
    <div
      style={popoverStyles}
      className="cookie-popover"
      role="region"
      aria-label="Cookie settings"
    >
      <div style={contentStyles}>
        <Font headingLevel={5} variant="bold" style={{ margin: 0, padding: 0 }}>
          Cookie Settings
        </Font>
        <Font size="small" style={{ margin: 0, padding: 0 }}>
          We use cookies to enhance your experience, analyze site traffic and deliver personalized
          content. Read our {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            label="Cookie Policy"
            onClick={handleOpenPolicy}
            size="body"
            className="cookie-policy-link"
            style={{
              display: 'inline',
              padding: 0,
              margin: 0,
            }}
            ref={policyTriggerRef}
          />
          .
        </Font>
      </div>
      <div style={actionsStyles}>
        <Button
          variant="tertiary"
          size="medium"
          text="Reject"
          onClick={handleDeclineCookies}
          style={{ width: '120px', display: 'flex', justifyContent: 'center' }}
        />
        <Button
          variant="primary"
          size="medium"
          text="Accept"
          onClick={handleAcceptCookies}
          style={{ width: '120px', display: 'flex', justifyContent: 'center' }}
        />
      </div>
    </div>
  )

  return (
    <>
      {popoverOpen &&
        typeof document !== 'undefined' &&
        ReactDOM.createPortal(popoverContent, document.body)}

      <Modal
        className="cookie-disclaimer-modal"
        closeOnOutsideClick
        onCloseModal={handleClosePolicy}
        showAuxiliaryButton={false}
        showLink={false}
        primaryButtonProps={{
          onClick: handleAcceptCookies,
          size: 'medium',
          text: 'Accept',
          variant: 'primary',
        }}
        secondaryButtonProps={{
          onClick: handleDeclineCookies,
          size: 'medium',
          text: 'Reject',
          variant: 'tertiary',
          style: { border: 'none' },
        }}
        showTertiaryButton={false}
        showModal={policyOpen}
        text=""
        title="Cookie Policy"
        type="fluid"
      >
        <Box className="cookie-policy-content">
          <Font size="small" style={{ marginBottom: '16px', display: 'block', fontSize: '14px' }}>
            Your privacy is important to us. We use a single cookie on this website to improve your
            experience by remembering one specific preference: the{' '}
            <Font variant="bold" style={{ fontSize: '14px' }}>
              Program/Market
            </Font>{' '}
            name you belong to.
          </Font>

          <Font
            size="small"
            variant="bold"
            style={{ marginBottom: '24px', marginTop: '24px', fontSize: '14px' }}
          >
            What the cookie does:
          </Font>
          <Font size="small" style={{ marginBottom: '16px', display: 'block', fontSize: '14px' }}>
            Stores your selected{' '}
            <Font variant="bold" style={{ fontSize: '14px' }}>
              Program/Market
            </Font>{' '}
            so we can present the correct content and settings when you return. No other personal or
            identifiable information is stored in this cookie.
          </Font>

          <Font
            size="small"
            variant="bold"
            style={{ marginBottom: '24px', marginTop: '24px', fontSize: '14px' }}
          >
            Retention & control:
          </Font>
          <Font size="small" style={{ marginBottom: '16px', display: 'block', fontSize: '14px' }}>
            The cookie is retained only as long as necessary to preserve your Program/Market
            preference. Use Cookie Settings within the application to &quot;Accept&quot; or
            &quot;Reject&quot; the cookie.
          </Font>

          <Font
            size="small"
            variant="bold"
            style={{ marginBottom: '24px', marginTop: '24px', fontSize: '14px' }}
          >
            Contact:
          </Font>
          <Font size="small" style={{ marginBottom: '16px', display: 'block', fontSize: '14px' }}>
            If you have questions about how we use cookies on this website or app, please contact:{' '}
            <Link
              label="DL-GLOBALAGROSUPPORT@pepsico.com"
              href="mailto:DL-GLOBALAGROSUPPORT@pepsico.com"
              size="body"
              style={{ fontSize: '14px' }}
            />
          </Font>
        </Box>
      </Modal>
    </>
  )
}

export default CookieDisclaimer
